import React, { useEffect, useState } from "react";
import { X, Plus, Trash2, Save, Info } from "lucide-react";
import { apiErrorMessage } from "../../api";

/**
 * Generic catalog manager modal (used for Roles + Departments).
 * Props:
 *   title, hint
 *   listFn / createFn / updateFn / deleteFn — the four api.xxx methods
 *   testid — root testid prefix
 *   onClose
 */
export default function CatalogModal({ title, hint, listFn, createFn, updateFn, deleteFn, testid, onClose }) {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => setItems(await listFn(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await createFn({ name: name.trim() }); setName(""); await load(); }
    catch (ex) { setErr(apiErrorMessage(ex, "Couldn't add")); }
    finally { setBusy(false); }
  };
  const rename = async (item, newName) => {
    if (!newName.trim() || newName === item.name) return;
    try { await updateFn(item.id, { name: newName.trim() }); await load(); }
    catch (ex) { setErr(apiErrorMessage(ex, "Couldn't rename")); }
  };
  const del = async (item) => {
    if (!window.confirm(`Archive "${item.name}"? It'll be removed from every profile.`)) return;
    await deleteFn(item.id);
    await load();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
           className="bg-white rounded-xl w-full max-w-lg p-6 space-y-4 max-h-[85vh] overflow-y-auto"
           data-testid={`${testid}-modal`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-display text-2xl">{title}</h2>
            <p className="text-xs text-[#6a736f] mt-1">{hint}</p>
          </div>
          <button onClick={onClose} className="text-[#6a736f]"><X size={18} /></button>
        </div>

        <form onSubmit={add} className="flex items-center gap-2 border border-cream rounded-lg p-2">
          <input
            required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Add a new one…"
            data-testid={`${testid}-new-input`}
            className="flex-1 border border-cream rounded-md px-3 py-1.5 text-sm"
          />
          <button
            type="submit" disabled={busy || !name.trim()}
            data-testid={`${testid}-add-btn`}
            className="btn-coral inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium disabled:opacity-40"
          >
            <Plus size={14} /> Add
          </button>
        </form>
        {err && <div className="text-xs text-red-600" data-testid={`${testid}-error`}>{err}</div>}

        <div className="border border-cream rounded-lg divide-y divide-[#EAF3EE]">
          {items.length === 0 ? (
            <div className="p-4 text-center text-sm text-[#6a736f]" data-testid={`${testid}-empty`}>
              <Info size={12} className="inline mr-1" />
              None yet — add one above.
            </div>
          ) : items.map((it) => (
            <TagRow key={it.id} item={it} onRename={rename} onDelete={del} testid={testid} />
          ))}
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="btn-outline-forest px-4 py-2 rounded-md text-sm">Done</button>
        </div>
      </div>
    </div>
  );
}

function TagRow({ item, onRename, onDelete, testid }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(item.name);
  useEffect(() => setVal(item.name), [item.name]);
  return (
    <div className="px-3 py-2 flex items-center gap-2" data-testid={`${testid}-row-${item.id}`}>
      {editing ? (
        <>
          <input
            value={val} onChange={(e) => setVal(e.target.value)} autoFocus
            className="flex-1 border border-cream rounded-md px-2 py-1 text-sm"
          />
          <button onClick={() => { onRename(item, val); setEditing(false); }}
                  className="text-forest"><Save size={14} /></button>
        </>
      ) : (
        <>
          <button onClick={() => setEditing(true)}
                  className="flex-1 text-left text-sm hover:text-coral">{item.name}</button>
          <button onClick={() => onDelete(item)}
                  data-testid={`${testid}-delete-${item.id}`}
                  className="text-[#6a736f] hover:text-red-600"><Trash2 size={14} /></button>
        </>
      )}
    </div>
  );
}
